import {
  GridCellParams,
  GridColDef,
  GridFilterOperator,
  GridRowModel,
  GridValidRowModel,
  GridValueOptionsParams,
  getGridDateOperators,
  getGridStringOperators
} from '@mui/x-data-grid-pro';
import { GroupedGridColDef } from './gridColumnTypes';
import { relativeDateOperators } from './relativeDateOperators';
import { CustomFieldDefinition, CustomFieldValue } from '../../../types';
import { sameAccount } from '../../../utils/accountIds';
import { recordCustomFieldEditable } from '../../../utils/customFieldEditability';
import { CustomFieldAcceptance, acceptedFor } from '../../../Api/Admin/CustomFields/useCustomFieldAcceptance';
import { upsertCustomFieldValuesApi } from '../../../Api/Admin/CustomFields/customFieldValuesApi';
import { extractApiErrorMessage } from '../McpAccess/extractApiErrorMessage';
import { isValid } from 'date-fns';
import { CustomFieldDatePicker } from '../CustomFieldDatePicker/CustomFieldDatePicker';
import {
  BOOLEAN_VALUE_OPTIONS,
  booleanLabel,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_DATE,
  DATA_TYPE_LIST,
  formatCustomFieldDay,
  isAcceptableCustomFieldDate,
  isUnreadableStoredDate,
  optionsIncluding,
  parseCustomFieldDate,
  serializeCustomFieldDate
} from '../../../utils/customFieldTypes';

export type CustomFieldRow = { custom_fields?: CustomFieldValue[] };

export const CUSTOM_FIELD_COLUMN_PREFIX = 'custom_field_';

export const FAILED_SAVE = 'Failed to save custom field.';

// Columns built by createCustomFieldColumns are keyed `custom_field_<defId>`; this is the
// canonical way to tell a custom-field column apart from a native/catalog one.
export const isCustomFieldColumn = (field: string): boolean => field.startsWith(CUSTOM_FIELD_COLUMN_PREFIX);

// Insert or update a custom field value in a row's custom_fields array, keyed by definition id.
export const upsertCustomFieldValue = (
  customFields: CustomFieldValue[] | undefined,
  def: Pick<CustomFieldDefinition, 'id' | 'name' | 'field_type' | 'position'> &
    Partial<Pick<CustomFieldDefinition, 'is_editable' | 'entity_type'>>,
  value: string
): CustomFieldValue[] => {
  const list = customFields ?? [];
  if (list.some((cf) => cf.definition_id === def.id)) {
    return list.map((cf) => (cf.definition_id === def.id ? { ...cf, value } : cf));
  }
  return [
    ...list,
    {
      definition_id: def.id,
      name: def.name,
      field_type: def.field_type,
      position: def.position,
      value,
      is_editable: def.is_editable,
      inherited: false,
      entity_type: def.entity_type
    }
  ];
};

type RowUpdateDefinition = Pick<CustomFieldDefinition, 'id'> &
  Partial<Pick<CustomFieldDefinition, 'entity_type' | 'field_type'>>;

// Risk is scored from dates on the server, so only a saved date can leave a row's risk stale.
export const savedDateMovesRisk = (saved: RowUpdateDefinition): boolean => saved.field_type === DATA_TYPE_DATE;

export interface CustomFieldRowUpdateOptions<T> {
  // Used for a definition that does not name its own; one grid can carry fields of more than one entity type.
  entityType: string;
  // A getter, not an array: callers hold definitions in a ref whose contents outlive the handler identity.
  getDefinitions: () => RowUpdateDefinition[];
  // Where the saved row goes — each surface owns a different store, which is the only real difference.
  persist: (row: T, saved: RowUpdateDefinition) => void;
  failureMessage?: string;
}

// The one custom-field grid save: find the definition whose value changed, PUT just that one, and
// hand the row back to the caller to store. Used as a DataGrid processRowUpdate on every surface
// with editable custom field columns.
export const makeCustomFieldRowUpdate =
  <T extends CustomFieldRow & { id?: string }>({
    entityType,
    getDefinitions,
    persist,
    failureMessage = FAILED_SAVE
  }: CustomFieldRowUpdateOptions<T>) =>
  async (newRow: GridValidRowModel, oldRow: GridValidRowModel): Promise<GridRowModel> => {
    const row = newRow as T;
    const valueFor = (record: T, definitionId: string) =>
      record.custom_fields?.find((cf) => cf.definition_id === definitionId)?.value ?? '';

    const changedDef = getDefinitions().find((def) => valueFor(row, def.id) !== valueFor(oldRow as T, def.id));
    if (!changedDef) return newRow;

    const { error } = await upsertCustomFieldValuesApi(row.id as string, changedDef.entity_type ?? entityType, [
      { custom_field_definition_id: changedDef.id, value: valueFor(row, changedDef.id) }
    ]);
    if (error) throw new Error(extractApiErrorMessage(error, failureMessage));

    persist(row, changedDef);
    return newRow;
  };

// Narrow on purpose: a caller holding only what a row carries builds the same columns as one holding the record.
export type CustomFieldColumnSource = Pick<CustomFieldDefinition, 'id' | 'name' | 'field_type' | 'position'> &
  Partial<Pick<CustomFieldDefinition, 'is_archived' | 'options' | 'is_editable' | 'entity_type'>>;

// The single text column a definition's values live in, read and written per row.
interface StoredValue<T> {
  read: (row: T) => string;
  write: (row: T, value: string) => T;
}

const storedValue = <T extends CustomFieldRow>(def: CustomFieldColumnSource): StoredValue<T> => ({
  read: (row) => row.custom_fields?.find((cf) => cf.definition_id === def.id)?.value ?? '',
  write: (row, value) => ({ ...row, custom_fields: upsertCustomFieldValue(row.custom_fields, def, value) })
});

// MUI reads emptiness off the parsed value, which a date it cannot show has none of.
const dateFilterOperators = <T extends CustomFieldRow>(stored: StoredValue<T>): GridFilterOperator[] => [
  ...getGridDateOperators().map((operator) => {
    if (operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty') return operator;
    const wantsEmpty = operator.value === 'isEmpty';

    return {
      ...operator,
      getApplyFilterFn: () => (_value: unknown, row: T) => (stored.read(row) === '') === wantsEmpty
    };
  }),
  ...relativeDateOperators()
];

// Date alone leaves the stored string behind, so its conversion is confined to one getter/setter
// pair. Yes/No stays on the string path — see the feature doc for what each type buys.
const columnForType = <T extends CustomFieldRow>(
  def: CustomFieldColumnSource,
  base: GridColDef,
  stored: StoredValue<T>
): GridColDef => {
  switch (def.field_type) {
    case DATA_TYPE_LIST:
      return { ...base, type: 'singleSelect', valueOptions: def.options?.choices ?? [] };
    case DATA_TYPE_BOOLEAN:
      return {
        ...base,
        type: 'singleSelect',
        // Per row, so a value the two options do not cover still shows in the editor rather than
        // opening blank. The filter panel asks without a row and gets the plain pair.
        valueOptions: ({ row }: GridValueOptionsParams<T>) =>
          optionsIncluding(BOOLEAN_VALUE_OPTIONS, row ? stored.read(row) : ''),
        // singleSelect blanks a value none of its options match; show what is stored instead.
        valueFormatter: (value: string) => booleanLabel(value)
      };
    case DATA_TYPE_DATE:
      return {
        ...base,
        type: 'date',
        valueGetter: (_value: unknown, row: T) => parseCustomFieldDate(stored.read(row)),
        valueSetter: ((value: Date | null | undefined, row: T): T => {
          // Only a day the picker can show replaces one it cannot; see the feature doc for why.
          if (isUnreadableStoredDate(stored.read(row)) && !isValid(value as Date)) return row;

          return stored.write(row, serializeCustomFieldDate(value));
        }) as GridColDef['valueSetter'],
        valueFormatter: (value: Date | null, row: T) => formatCustomFieldDay(value) || stored.read(row),
        filterOperators: dateFilterOperators(stored),
        // An unacceptable day holds the cell open rather than committing over what is there.
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: !isAcceptableCustomFieldDate(params.props.value as Date | null)
        }),
        renderEditCell: ({ api, id, field, value, hasFocus }) => (
          <CustomFieldDatePicker
            value={value as Date | null}
            variant="standard"
            takeFocus={hasFocus}
            onChange={(picked) => api.setEditCellValue({ id, field, value: picked })}
          />
        )
      };
    default:
      return { ...base, type: 'string', filterOperators: getGridStringOperators() };
  }
};

// Builds editable grid columns for a set of custom field definitions. Works for any row type
// that carries a custom_fields array (Inventory assets, Projects, …). These columns are appended after
// the native-column account filter runs, so account-hidden ones (keyed custom_field_<defId>) must be
// dropped here too; default [] no-ops for callers without an account config.
export const createCustomFieldColumns = <T extends CustomFieldRow = CustomFieldRow>(
  defs: CustomFieldColumnSource[],
  accountDisabledFields: string[] = []
): GroupedGridColDef[] =>
  defs
    .filter((d) => !d.is_archived && !accountDisabledFields.includes(`${CUSTOM_FIELD_COLUMN_PREFIX}${d.id}`))
    .sort((a, b) => a.position - b.position)
    .map((def) => {
      const stored = storedValue<T>(def);
      const base: GridColDef = {
        field: `${CUSTOM_FIELD_COLUMN_PREFIX}${def.id}`,
        headerName: def.name,
        sortable: true,
        filterable: true,
        editable: true,
        width: 200,
        description: 'Drag to move column',
        valueGetter: (_value: unknown, row: T) => stored.read(row),
        valueSetter: ((value: string, row: T): T => stored.write(row, value)) as GridColDef['valueSetter']
      };

      // Cast: GroupedGridColDef is typed as GridSingleSelectColDef for historical reasons.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return { ...columnForType(def, base, stored), columnGroup: 'Ungrouped' } as unknown as GroupedGridColDef;
    });

export interface CustomFieldAccountBinding<T> {
  // The account these definitions were fetched for; every row in the payload carries its set, whoever owns the row.
  definitionsAccountId: string | null;
  // The row's own account, resolved the way that grid's rows name it; undefined where the grid cannot.
  rowAccountId: (row: T) => string | undefined;
  // What the api answered for the other accounts on show, since a row account's parent is not derivable here.
  acceptance: CustomFieldAcceptance;
  // Passed in rather than read here: store/User reaches the col-defs that reach this module.
  unrestricted: boolean;
  // Whether the user's permission sets withhold custom_field_values#upsert; it locks every custom-field cell.
  denied: boolean;
  // The definitions the columns were built from, so a row carrying no entry for one still knows its lock.
  definitions: Pick<CustomFieldDefinition, 'id' | 'is_editable'>[];
}

// The api pairs a definition with the account of the record it is written on, so a row from any other account is offered an edit that can only 422.
// Grid-level rather than per column because MUI's column `editable` is one boolean for every row.
export const customFieldCellEditable =
  <T,>(binding: CustomFieldAccountBinding<T>) =>
  (params: GridCellParams): boolean => {
    if (!isCustomFieldColumn(params.field)) return true;

    const definitionId = params.field.slice(CUSTOM_FIELD_COLUMN_PREFIX.length);
    const row = params.row as CustomFieldRow;
    const definition = {
      id: definitionId,
      is_editable: binding.definitions.find((def) => def.id === definitionId)?.is_editable
    };
    if (!recordCustomFieldEditable(row.custom_fields, definition, binding.unrestricted, binding.denied)) return false;

    const rowAccountId = binding.rowAccountId(params.row as T);
    // A grid that cannot name a row's account keeps the edit it has always offered rather than guessing.
    if (!rowAccountId) return true;
    if (sameAccount(rowAccountId, binding.definitionsAccountId)) return true;

    const accepted = acceptedFor(binding.acceptance, rowAccountId);
    // Still asking, or asked and unanswered: let the write try and report rather than disable on an answer we lack.
    if (!accepted) return true;
    return accepted.has(definitionId);
  };

// A grid carrying fields of more than one entity type: each custom-field column is judged by the binding whose
// definitions include it, so one type's failed lookup never loosens another type's cells. The first binding answers
// for every other column.
export const customFieldCellEditableByType = <T,>(bindings: CustomFieldAccountBinding<T>[]) => {
  const gates = bindings.map((binding) => ({
    definitionIds: new Set(binding.definitions.map((def) => def.id)),
    editable: customFieldCellEditable(binding)
  }));
  return (params: GridCellParams): boolean => {
    const definitionId = params.field.slice(CUSTOM_FIELD_COLUMN_PREFIX.length);
    return (gates.find((gate) => gate.definitionIds.has(definitionId)) ?? gates[0]).editable(params);
  };
};
